import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  sendEmailVerification,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate, useLocation } from 'react-router-dom';
import Preloader from '../components/Preloader';
import { useAuth } from '../contexts/AuthContext';

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getSignInErrorMessage = (error) => {
  if (error?.code === 'auth/network-request-failed') {
    return 'CoursEngage could not reach the sign-in server. Please check your internet connection and try again.';
  }

  if (error?.code === 'auth/invalid-credential' || error?.code === 'auth/wrong-password') {
    return 'The email or password is incorrect.';
  }

  if (error?.code === 'auth/user-not-found') {
    return 'No account was found for that email address.';
  }

  if (error?.message) {
    return error.message.replace('Firebase', 'CoursEngage');
  }

  return 'CoursEngage could not sign you in. Please try again.';
};

function SignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loadingState, setLoadingState] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);


  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();

  const from = location.state?.from?.pathname || '/';

  useEffect(() => {
    if (!loading && user.isAuthenticated) {
      if (user.role === 'professor') {
        navigate('/professor', { replace: true });
      } else if (user.role === 'student') {
        navigate('/student', { replace: true });
      } else if (user.role === 'admin') {
        navigate('/admin', { replace: true });
      } else {
        navigate('/default', { replace: true });
      }
    }
  }, [user, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setLoadingState(true);
    setErrorMessage('');
    const trimmedEmail = email.trim();

    try {
      let userCredential;
      try {
        userCredential = await signInWithEmailAndPassword(auth, trimmedEmail, password);
      } catch (error) {
        if (error?.code !== 'auth/network-request-failed') {
          throw error;
        }

        await wait(700);
        userCredential = await signInWithEmailAndPassword(auth, trimmedEmail, password);
      }
      const currentUser = userCredential.user;

      if (!currentUser.emailVerified) {
       setPendingUser(currentUser);
       setShowVerificationModal(true);
       await signOut(auth);
       setLoadingState(false);
       return;
     }

      const userDocRef = doc(db, 'users', currentUser.uid);
      const docSnapshot = await getDoc(userDocRef);
      if (!docSnapshot.exists()) {
        alert('User data not found.');
        await signOut(auth);
      }
    } catch (error) {
      console.error('Error during sign-in:', error);
      setErrorMessage(getSignInErrorMessage(error));
    } finally {
      setLoadingState(false);
    }
  };

  const handleResendVerification = async () => {
    if (pendingUser) {
      const actionCodeSettings = {
        url: window.location.origin + '/sign-in',
        handleCodeInApp: true,
      };
      try {
        await sendEmailVerification(pendingUser, actionCodeSettings);
        alert('Verification email resent. Please check your inbox.');
      } catch (error) {
        console.error("Error resending verification email:", error);
        setErrorMessage("Error resending verification email.");
      }
    }
    setShowVerificationModal(false);
    setPendingUser(null);
  };


  const handleForgotPassword = async (e) => {
    e.preventDefault();
    const emailAddress = prompt('Please enter your email address to reset your password:');
    if (emailAddress) {
      try {
        await sendPasswordResetEmail(auth, emailAddress);
        alert('Password reset email sent! Please check your inbox.');
      } catch (error) {
        console.error('Error sending password reset email:', error);
        setErrorMessage(error.message.replace('Firebase', 'CoursEngage'));
      }
    }
  };

  if (loading) {
    return <Preloader />;
  }

  return (

    <main
        className="min-h-screen"
        style={{
          backgroundColor: "var(--bg-color)",
          color: "var(--text-color)",
        }}
      >
    <section
      className="flex item-center justify-center px-4 pt-20"
      style={{ backgroundColor: 'var(--bg-color)', color: 'var(--text-color)' }}
    >
      <div
        className="w-full max-w-md p-8 rounded-2xl shadow-2xl"
        style={{ backgroundColor: 'var(--card-bg)' }}
      >
        <h1 className="text-3xl font-bold text-center mb-6">Sign In</h1>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label htmlFor="email" className="block mb-1 text-sm font-medium">
              Email address
            </label>
            <input
              type="email"
              id="email"
              placeholder="Email address"
              className="w-full px-3 py-2 border border-[var(--border-color)] 
                         rounded-md bg-[var(--input-bg)] text-[var(--text-color)]
                         focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                         focus:border-transparent transition-all"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="password" className="block mb-1 text-sm font-medium">
              Password
            </label>
            <input
              type="password"
              id="password"
              placeholder="Password"
              className="w-full px-3 py-2 border border-[var(--border-color)]
                         rounded-md bg-[var(--input-bg)] text-[var(--text-color)]
                         focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]
                         focus:border-transparent transition-all"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <button
            type="submit"
            className="w-full py-2 mt-2 rounded-md font-semibold
                       bg-[var(--primary-color)] text-white
                       hover:bg-[var(--hover-color)]
                       transition-colors disabled:opacity-60"
            disabled={loadingState}
          >
            {loadingState ? 'Signing in...' : 'Sign in'}
          </button>

          {errorMessage && (
            <div className="text-red-500 text-center mt-2">{errorMessage}</div>
          )}
        </form>

        <div className="mt-4 text-center">
          <p>
            Don’t have an account?{' '}
            <a href="/sign-up" className="text-[var(--primary-color)] hover:underline">
              Create One
            </a>
          </p>
          <p className="mt-2">
            Forgot Password?{' '}
            <button onClick={handleForgotPassword} className="text-[var(--primary-color)] hover:underline">
              Reset Password
            </button>
          </p>
        </div>
      </div>
    </section>
    {showVerificationModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-[var(--card-bg)] p-6 rounded-lg shadow-lg max-w-sm mx-auto">
            <h2 className="text-xl font-bold mb-4">Email Verification Required</h2>
            <p className="mb-4">
              Your email is not verified. Please verify your email by clicking the link in the verification email.
              If you haven't received it, click below to resend the verification email.
            </p>
            <button
              onClick={handleResendVerification}
              className="w-full py-2 mb-2 rounded-md font-semibold bg-[var(--primary-color)] text-white hover:bg-[var(--hover-color)] transition-colors"
            >
              Resend Verification Email
            </button>
            <button
              onClick={() => {
                setShowVerificationModal(false);
                setPendingUser(null);
              }}
              className="w-full py-2 rounded-md font-semibold bg-gray-500 text-white hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </main>
  );
}

export default SignIn;
